import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Video, Radio,
  User, CalendarDays, Clapperboard,
  Building2, UserCheck, Briefcase,
  Mail, Phone, FileText, MapPin,
  Users, GraduationCap, Star,
  MessageSquare, ArrowRight, ArrowLeft,
  Check, Loader2, ChevronLeft, ChevronRight,
  Clock, PartyPopper,
} from 'lucide-react';
import './BookingForm.css';

const API_URL = 'http://localhost/reactwebmedia/backend/submit_request.php';

const SERVICES = [
  { name: 'photography',    Icon: Camera, label: 'Photography' },
  { name: 'videography',    Icon: Video,  label: 'Videography' },
  { name: 'live_streaming', Icon: Radio,  label: 'Live Streaming' },
];

const STEPS = [
  { id: 1, label: 'Requester', Icon: User },
  { id: 2, label: 'Event',     Icon: CalendarDays },
  { id: 3, label: 'Services',  Icon: Clapperboard },
];

const EMPTY = {
  request_side: '', requested_person: '', designation: '',
  email: '', phone: '',
  event_name: '', event_date: '', event_time: '', location: '',
  staff_count: '', student_count: '', notable_invitees: '',
  photography: false, videography: false, live_streaming: false,
  remarks: '',
};

const MONTHS = ['January','February','March','April','May','June',
                 'July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const HOURS  = Array.from({length:12},(_,i)=>String(i+1).padStart(2,'0'));
const MINS   = Array.from({length:60},(_,i)=>String(i).padStart(2,'0'));
const AMPM   = ['AM','PM'];

/* ─── Helpers outside parent ─────────────────────────────── */
function Field({ label, required, icon: IconComp, children }) {
  return (
    <div className="bf-field">
      <label className="bf-label">
        {IconComp && <IconComp size={13} className="bf-label-icon" />}
        {label}{required && <span className="bf-req">*</span>}
      </label>
      {children}
    </div>
  );
}
function BfInput({ name, type='text', value, onChange, ...rest }) {
  return (
    <input className="bf-input" id={name} name={name} type={type}
      value={value} onChange={onChange} autoComplete="off" spellCheck="false" {...rest} />
  );
}
function BfTextarea({ name, rows=3, value, onChange, placeholder }) {
  return (
    <textarea className="bf-input bf-textarea" id={name} name={name}
      rows={rows} value={value} onChange={onChange} placeholder={placeholder} />
  );
}

/* ─── Custom Calendar ─────────────────────────────────────── */
function CustomCalendar({ value, onChange }) {
  const today  = new Date();
  const parsed = value ? new Date(value + 'T00:00:00') : null;
  const [view, setView] = useState({
    month: parsed ? parsed.getMonth() : today.getMonth(),
    year:  parsed ? parsed.getFullYear() : today.getFullYear(),
  });

  const prevMonth = () => setView(v => {
    const d = new Date(v.year, v.month - 1, 1);
    return { month: d.getMonth(), year: d.getFullYear() };
  });
  const nextMonth = () => setView(v => {
    const d = new Date(v.year, v.month + 1, 1);
    return { month: d.getMonth(), year: d.getFullYear() };
  });

  const firstDay  = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells = Array.from({ length: firstDay + daysInMonth }, (_, i) =>
    i < firstDay ? null : i - firstDay + 1
  );

  const selectDay = (day) => {
    if (!day) return;
    const m = String(view.month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange(`${view.year}-${m}-${d}`);
  };

  const isSelected = (day) => {
    if (!day || !parsed) return false;
    return parsed.getFullYear() === view.year &&
           parsed.getMonth()    === view.month &&
           parsed.getDate()     === day;
  };
  const isToday = (day) => {
    if (!day) return false;
    return today.getFullYear() === view.year &&
           today.getMonth()    === view.month &&
           today.getDate()     === day;
  };
  const isPast = (day) => {
    if (!day) return false;
    const d = new Date(view.year, view.month, day);
    d.setHours(0,0,0,0);
    const t = new Date(); t.setHours(0,0,0,0);
    return d < t;
  };

  return (
    <div className="bf-cal">
      <div className="bf-cal-header">
        <button type="button" className="bf-cal-nav" onClick={prevMonth}>
          <ChevronLeft size={16} />
        </button>
        <span className="bf-cal-title">
          {MONTHS[view.month]} {view.year}
        </span>
        <button type="button" className="bf-cal-nav" onClick={nextMonth}>
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="bf-cal-grid bf-cal-daynames">
        {DAYS.map(d => <span key={d} className="bf-cal-dayname">{d}</span>)}
      </div>
      <div className="bf-cal-grid">
        {cells.map((day, i) => (
          <button
            key={i} type="button"
            className={`bf-cal-cell
              ${!day          ? 'bf-cal-empty'    : ''}
              ${isSelected(day) ? 'bf-cal-selected' : ''}
              ${isToday(day)    ? 'bf-cal-today'    : ''}
              ${isPast(day)     ? 'bf-cal-past'     : ''}
            `}
            onClick={() => selectDay(day)}
            disabled={isPast(day)}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Custom Time Picker ──────────────────────────────────── */
function CustomTimePicker({ value, onChange }) {
  const parse = (v) => {
    if (!v) return { h: '08', m: '00', ap: 'AM' };
    const [hh, mm] = v.split(':');
    const h24 = parseInt(hh, 10);
    const ap  = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return { h: String(h12).padStart(2,'0'), m: mm, ap };
  };

  const { h, m, ap } = parse(value);

  const emit = (nh, nm, nap) => {
    let h24 = parseInt(nh, 10);
    if (nap === 'PM' && h24 !== 12) h24 += 12;
    if (nap === 'AM' && h24 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2,'0')}:${nm}`);
  };

  const ColScroll = ({ items, selected, onSelect, itemClass }) => {
    const ref = useRef(null);
    useEffect(() => {
      const idx = items.indexOf(selected);
      if (ref.current && idx >= 0) {
        ref.current.scrollTop = idx * 40 - 40;
      }
    },);
    return (
      <div className="bf-tp-col" ref={ref}>
        {items.map(it => (
          <button key={it} type="button"
            className={`bf-tp-item ${itemClass || ''} ${it === selected ? 'bf-tp-active' : ''}`}
            onClick={() => onSelect(it)}
          >{it}</button>
        ))}
      </div>
    );
  };

  return (
    <div className="bf-tp">
      <div className="bf-tp-label-row">
        <span><Clock size={14}/> Select Time</span>
      </div>
      <div className="bf-tp-cols">
        <div className="bf-tp-col-wrap">
          <div className="bf-tp-col-title">Hour</div>
          <ColScroll items={HOURS} selected={h}
            onSelect={nh => emit(nh, m, ap)} />
        </div>
        <div className="bf-tp-sep">:</div>
        <div className="bf-tp-col-wrap">
          <div className="bf-tp-col-title">Min</div>
          <ColScroll items={MINS} selected={m}
            onSelect={nm => emit(h, nm, ap)} />
        </div>
        <div className="bf-tp-col-wrap">
          <div className="bf-tp-col-title">AM/PM</div>
          <ColScroll items={AMPM} selected={ap}
            onSelect={nap => emit(h, m, nap)} />
        </div>
      </div>
      {value && (
        <div className="bf-tp-preview">
          Selected: <strong>{h}:{m} {ap}</strong>
        </div>
      )}
    </div>
  );
}

/* ─── Success Modal ───────────────────────────────────────── */
function SuccessModal({ countdown }) {
  return (
    <div className="bf-modal-backdrop">
      <div className="bf-modal">
        <div className="bf-modal-icon">
          <PartyPopper size={40} />
        </div>
        <h2 className="bf-modal-title">Request Sent!</h2>
        <p className="bf-modal-body">
          Your media booking request has been successfully submitted.<br />
          The media unit will review and confirm your request shortly.
        </p>
        <div className="bf-modal-countdown">
          Returning to home in <span>{countdown}s</span>
        </div>
        <div className="bf-modal-bar">
          <div className="bf-modal-bar-fill" style={{ animationDuration: '4s' }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────── */
export default function BookingForm() {
  const navigate = useNavigate();
  const [formData,  setFormData]  = useState(EMPTY);
  const [loading,   setLoading]   = useState(false);
  const [step,      setStep]      = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(4);
  const [error,     setError]     = useState('');

  /* countdown + auto-navigate after success */
  useEffect(() => {
    if (!showModal) return;
    if (countdown <= 0) { navigate('/'); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showModal, countdown, navigate]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const handleDateChange  = useCallback((v) => setFormData(p => ({ ...p, event_date: v })), []);
  const handleTimeChange  = useCallback((v) => setFormData(p => ({ ...p, event_time: v })), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res    = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const result = await res.json();
      if (result.success) {
        setFormData(EMPTY);
        setStep(1);
        setCountdown(4);
        setShowModal(true);
      } else {
        setError(result.message || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bf-root">
      <div className="bf-bg" />
      <div className="bf-overlay" />

      {/* Film strips */}
      <div className="bf-film-strip bf-film-left">
        {Array.from({length:16}).map((_,i) => <div key={i} className="bf-film-hole" />)}
      </div>
      <div className="bf-film-strip bf-film-right">
        {Array.from({length:16}).map((_,i) => <div key={i} className="bf-film-hole" />)}
      </div>

      {/* Particles */}
      <div className="bf-particles">
        {Array.from({length:12}).map((_,i) => (
          <div key={i} className={`bf-particle bfp${i+1}`} />
        ))}
        <div className="bf-orb bf-orb1" />
        <div className="bf-orb bf-orb2" />
        <div className="bf-orb bf-orb3" />
      </div>

      {/* Success Modal */}
      {showModal && <SuccessModal countdown={countdown} />}

      <div className="bf-wrap">

        {/* Header */}
        <div className="bf-header">
          <div className="bf-badge">
            <span className="bf-badge-dot" />
            Media Unit · University of Peradeniya 
          </div>
          <h1 className="bf-title">
            Media Booking<br />
            <span className="bf-title-accent">Request Form</span>
          </h1>
          <p className="bf-subtitle">
            Fill in your details to request professional media coverage for your event.
          </p>
        </div>

        {/* Steps */}
        <div className="bf-steps">
          {STEPS.map((s, i) => (
            <div key={s.id} className="bf-step-wrap">
              <button type="button"
                className={`bf-step ${step===s.id?'active':''} ${step>s.id?'done':''}`}
                onClick={() => setStep(s.id)}
              >
                <span className="bf-step-icon">
                  {step > s.id
                    ? <Check size={18} strokeWidth={2.5} />
                    : <s.Icon size={18} strokeWidth={1.8} />}
                </span>
                <span className="bf-step-label">{s.label}</span>
              </button>
              {i < STEPS.length-1 && (
                <div className={`bf-step-line ${step>s.id?'done':''}`} />
              )}
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bf-message bf-message--error">
            <span className="bf-msg-icon">✕</span>{error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bf-form">

          {/* Step 1 */}
          {step === 1 && (
            <div className="bf-section bf-section--in">
              <div className="bf-section-title">
                <span className="bf-section-num">01</span>Requester Information
              </div>
              <div className="bf-grid-2">
                <Field label="Request Side" icon={Building2} required>
                  <BfInput name="request_side" value={formData.request_side}
                    onChange={handleChange} placeholder="e.g. Faculty of Computing" required />
                </Field>
                <Field label="Requested Person" icon={UserCheck} required>
                  <BfInput name="requested_person" value={formData.requested_person}
                    onChange={handleChange} placeholder="Full Name (Dean / Director / HoD)" required />
                </Field>
                <Field label="Designation" icon={Briefcase} required>
                  <BfInput name="designation" value={formData.designation}
                    onChange={handleChange} placeholder="e.g. Dean / Director / HoD" required />
                </Field>
                <Field label="Email Address" icon={Mail} required>
                  <BfInput name="email" type="email" value={formData.email}
                    onChange={handleChange} placeholder="you@gmail.com" required />
                </Field>
                <Field label="Phone Number" icon={Phone}>
                  <BfInput name="phone" type="tel" value={formData.phone}
                    onChange={handleChange} placeholder="07X XXX XXXX" />
                </Field>
              </div>
              <div className="bf-step-nav">
                <button type="button" className="bf-btn-next" onClick={() => setStep(2)}>
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="bf-section bf-section--in">
              <div className="bf-section-title">
                <span className="bf-section-num">02</span>Event Details
              </div>
              <div className="bf-grid-2">
                <Field label="Event Name" icon={FileText} required>
                  <BfInput name="event_name" value={formData.event_name}
                    onChange={handleChange} placeholder="Annual Awards Ceremony" required />
                </Field>
                <Field label="Location" icon={MapPin}>
                  <BfInput name="location" value={formData.location}
                    onChange={handleChange} placeholder="Main Auditorium" />
                </Field>
                <Field label="Number of Staff" icon={Users}>
                  <BfInput name="staff_count" type="number" min="0"
                    value={formData.staff_count} onChange={handleChange} placeholder="0" />
                </Field>
                <Field label="Number of Students" icon={GraduationCap}>
                  <BfInput name="student_count" type="number" min="0"
                    value={formData.student_count} onChange={handleChange} placeholder="0" />
                </Field>
              </div>

              {/* Custom Date Picker */}
              <Field label="Event Date" icon={CalendarDays} required>
                <CustomCalendar value={formData.event_date} onChange={handleDateChange} />
              </Field>

              {/* Custom Time Picker */}
              <Field label="Event Time" icon={Clock} required>
                <CustomTimePicker value={formData.event_time} onChange={handleTimeChange} />
              </Field>

              <Field label="Notable Invitees" icon={Star}>
                <BfTextarea name="notable_invitees" rows={2}
                  value={formData.notable_invitees} onChange={handleChange}
                  placeholder="e.g. Minister / Governor / VC / Dean / Etc." />
              </Field>
              <div className="bf-step-nav bf-step-nav--two">
                <button type="button" className="bf-btn-back" onClick={() => setStep(1)}>
                  <ArrowLeft size={16} /> Back
                </button>
                <button type="button" className="bf-btn-next" onClick={() => setStep(3)}>
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div className="bf-section bf-section--in">
              <div className="bf-section-title">
                <span className="bf-section-num">03</span>Services &amp; Remarks
              </div>
              <p className="bf-service-hint">Select all media services you need for your event</p>
              <div className="bf-service-cards">
                {SERVICES.map(({ name, Icon, label }) => (
                  <label key={name}
                    className={`bf-service-card ${formData[name]?'selected':''}`}>
                    <input type="checkbox" name={name} checked={formData[name]}
                      onChange={handleChange} className="bf-service-check" />
                    <span className="bf-service-icon"><Icon size={30} strokeWidth={1.5} /></span>
                    <span className="bf-service-name">{label}</span>
                    <span className="bf-service-tick"><Check size={11} strokeWidth={3} /></span>
                  </label>
                ))}
              </div>
              <Field label="Remarks / Special Instructions" icon={MessageSquare}>
                <BfTextarea name="remarks" rows={4} value={formData.remarks}
                  onChange={handleChange}
                  placeholder="Any special requirements or instructions..." />
              </Field>
              <div className="bf-step-nav bf-step-nav--two">
                <button type="button" className="bf-btn-back" onClick={() => setStep(2)}>
                  <ArrowLeft size={16} /> Back
                </button>
                <button type="submit" className="bf-btn-submit" disabled={loading}>
                  {loading
                    ? <><Loader2 size={16} className="bf-spin" /> Submitting…</>
                    : <><span>Submit Request</span><ArrowRight size={16} /></>
                  }
                </button>
              </div>
            </div>
          )}

        </form>
      </div>
    </div>
  );
}